import {
  ActionIcon,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Pagination,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import React from "react";

import { notifications } from "@mantine/notifications";
import {
  IconEdit,
  IconPlus,
  IconPrinter,
  IconTrash,
} from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import {
  deleteSupplierOrderById,
  getOrders,
  togglePrintState,
} from "api/supplier.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
} from "common/constants";
import { navigateTo } from "common/navigation";
import {
  confirmDelete,
  formatDate,
  formatNZD,
  formatPercent,
  truncate,
} from "common/utils";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import Reprint from "pages/admin/components/Reprint";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PaginatedSupplierOrders } from "types/model.types";
import AdminSupplierOrderEntryPage from "./AdminSupplierOrderEntryPage";

const AdminSupplierOrderPage: React.FC = () => {
  const navigate = useNavigate();
  //const apiFetchRef = React.useRef<boolean>(false);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [entries, setEntries] = useState<PaginatedSupplierOrders>({
    data: [],
    total: 0,
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [reloadTrigger, setReloadTrigger] = useState(new Date());
  const [loading, setLoading] = useState<boolean>(true);
  const [activePage, setActivePage] = useState(1);
  //const [exportData, setExportData] = useState<Supplier | null>(null);
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [params] = useSearchParams();
  const id = params.get("id");

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const fetchData = async () => {
      try {
        const response = await safeApiCall(
          getOrders(activePage, DEFAULT_PAGE_SIZE),
        );
        if (!mounted) return;

        if (response) {
          setEntries(response);
        }
      } catch (err) {
        logError(err);
        notifications.show({
          message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [activePage, reloadTrigger]);

  useEffect(() => {
    if (!id) return;

    handleEdit(id);
    setTimeout(() => {
      navigate("/admin/supplier-orders", { replace: true });
    }, 500);
  }, [id]);

  const handleEdit = async (recordId: string | number) => {
    setOrderId(Number(recordId));
    setEntryModalOpen(true);
  };
  const handleDelete = async (id: string | number) => {
    if (!id) return;
    if (confirmDelete() === false) return;
    try {
      await safeApiCall(deleteSupplierOrderById(Number(id)));
      setReloadTrigger(new Date());
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
        message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Success,
      });
    } catch {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
        message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
    }
  };
  const handlePreview = async (
    recordId: string | number,
    previewType: "costing" | "prices",
  ) => {
    await safeApiCall(togglePrintState(Number(recordId), previewType));
    setOrderId(Number(recordId));
    navigateTo(
      `/print/supplier-order?id=${Number(recordId)}&t=${previewType}&e=1`,
    );
  };

  const rows = entries.data.map((entry) => {
    return (
      <Table.Tr key={entry.id}>
        <Table.Td className="action-cell-lg2">
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Print Prices">
              <ActionIcon
                variant="subtle"
                color="blue"
                onClick={() => handlePreview(entry.id, "prices")}
              >
                <IconPrinter size={18} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Print Costings">
              <ActionIcon
                variant="subtle"
                color="blue"
                onClick={() => handlePreview(entry.id, "costing")}
              >
                <IconPrinter size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Edit">
              <ActionIcon
                variant="subtle"
                color="blue"
                disabled={entry.isProcessed}
                onClick={() => handleEdit(entry.id)}
              >
                <IconEdit size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Delete">
              <ActionIcon
                variant="subtle"
                color="blue"
                disabled={entry.isProcessed}
                onClick={() => handleDelete(entry.id)}
              >
                <IconTrash size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Table.Td>
        <Table.Td>{entry.id}</Table.Td>
        <Table.Td>{formatDate(entry.createdOn)}</Table.Td>
        <Table.Td>
          {entry.isProcessed
            ? "Processed"
            : entry.isOrdersReceived
              ? "Received"
              : "Pending"}
        </Table.Td>
        <Table.Td>{entry.supplierName}</Table.Td>
        <Table.Td className="money">{formatNZD(entry.exchange)}</Table.Td>
        <Table.Td className="money">{formatPercent(entry.commission)}</Table.Td>
        <Table.Td className="money">{formatNZD(entry.freight)}</Table.Td>
        <Table.Td title={entry.notes ?? ""}>
          {truncate(entry.notes, undefined, 55)}
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Stack>
      <Title order={2}>Supplier Orders</Title>
      {loading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <>
          <Group justify="space-between" wrap="nowrap" mb="md">
            <Group justify="flex-start" wrap="nowrap" mb="md">
              <Reprint
                isSupplierOrderPage={true}
                onReprint={(
                  id: number,
                  orderStage?: number | string | undefined,
                ) => {
                  navigateTo(
                    `print/supplier-order?id=${id}&t=${orderStage}&e=1`,
                  );
                }}
              />
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => {
                  setOrderId(null);
                  setEntryModalOpen(true);
                }}
              >
                Add Order
              </Button>
            </Group>

            <Group justify="flex-end" wrap="nowrap" mb="md">
              <Text size="sm" c="dimmed">
                Showing 30 items per page
              </Text>
              <Pagination
                total={Math.ceil(entries.total / DEFAULT_PAGE_SIZE)}
                value={activePage}
                onChange={setActivePage}
                siblings={1}
                boundaries={1}
              />
            </Group>
          </Group>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ whiteSpace: "nowrap" }}>Actions</Table.Th>
                <Table.Th>Order #</Table.Th>
                <Table.Th>Date</Table.Th>
                <Table.Th>Order Stage</Table.Th>
                <Table.Th>Supplier</Table.Th>
                <Table.Th className="money">Exchange</Table.Th>
                <Table.Th className="money">Commission</Table.Th>
                <Table.Th className="money">Freight</Table.Th>
                <Table.Th style={{ minWidth: 300 }}>Notes</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Pagination
              total={Math.ceil(entries.total / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
        </>
      )}
      <Modal
        opened={entryModalOpen}
        onClose={() => {
          setEntryModalOpen(false);
          setReloadTrigger(new Date());
        }}
        fullScreen
        title={`${(orderId ?? 0) > 0 ? "Edit" : "Add"}  Supplier Order`}
        size="xl"
        centered
        closeOnEscape={false}
        withinPortal={true} // default, but explicit here
        zIndex={2001} // ensure it's above layout elements
      >
        <AdminSupplierOrderEntryPage
          recordId={orderId ?? 0}
          onSaveComplete={() => {
            setEntryModalOpen(false);
          }}
        />
      </Modal>
    </Stack>
  );
};

export default AdminSupplierOrderPage;
